import { Box, useMediaQuery } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import type { JackpotPool } from '../../../@types';
import { useSkinStyles } from '../../../hooks/useSkinStyles';
import { JACKPOT_WINNER_ANIMATION_SKIN_STYLES } from '../../../styles/components/jackpotWinnerAnimation';

const VIDEO_EDGE_FADE = [
  'linear-gradient(to right, transparent 0%, #000 5%, #000 95%, transparent 100%)',
  'linear-gradient(to bottom, transparent 0%, #000 5%, #000 95%, transparent 100%)',
].join(', ');

const styles = {
  artwork: {
    position: 'relative',
    zIndex: 1,
    width: { xs: 'min(100%, 390px)', md: 592 },
    maxWidth: '100%',
    mb: { xs: -1.5, md: -3 },
    pointerEvents: 'none',
  },
  videoFrame: {
    position: 'relative',
    zIndex: 1,
    display: 'block',
    width: { xs: '100%', md: 746 },
    maxWidth: '100%',
    mb: { xs: -1.5, md: -3 },
    pointerEvents: 'none',
    // One element masks all four edges (intersecting both fades) so the video pays for a single mask layer.
    maskImage: VIDEO_EDGE_FADE,
    maskComposite: 'intersect',
    WebkitMaskImage: VIDEO_EDGE_FADE,
    WebkitMaskComposite: 'source-in',
  },
  video: {
    display: 'block',
    width: '100%',
    aspectRatio: '16 / 9',
    objectFit: 'contain',
  },
} as const;

type JackpotWinnerArtProps = {
  winningJackpotPool: JackpotPool;
};

const JackpotWinnerArt = ({ winningJackpotPool }: JackpotWinnerArtProps) => {
  const { fallbackArtworkSrc, videoSrc } = useSkinStyles(JACKPOT_WINNER_ANIMATION_SKIN_STYLES);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [hasPlaybackError, setHasPlaybackError] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const previousWinRef = useRef(winningJackpotPool);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (previousWinRef.current !== winningJackpotPool) {
      previousWinRef.current = winningJackpotPool;
      video.currentTime = 0;
    }

    void Promise.resolve()
      .then(() => video.play())
      .catch(() => setHasPlaybackError(true));
  }, [winningJackpotPool]);

  if (prefersReducedMotion || hasPlaybackError) {
    return (
      <Box
        component='img'
        src={fallbackArtworkSrc}
        alt=''
        aria-hidden='true'
        sx={styles.artwork}
        data-testid='jackpot-winner-art'
      />
    );
  }

  return (
    <Box sx={styles.videoFrame} data-testid='jackpot-winner-video-frame'>
      <Box
        component='video'
        ref={videoRef}
        src={videoSrc}
        autoPlay
        muted
        playsInline
        preload='auto'
        aria-hidden='true'
        onError={() => setHasPlaybackError(true)}
        sx={styles.video}
        data-testid='jackpot-winner-video'
      />
    </Box>
  );
};

export default JackpotWinnerArt;
